import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  ChangePasswordCard, ProfileCard, useMe, useTheme,
  IconBlase, IconCalendar, IconCheck, IconEuro, IconFile, IconFolder, IconGear, IconLaster,
  IconListe, IconMore, IconTeam, IconUser, IconUsers,
} from "@vimaana/ui";
import FotoEingangPage from "../pages/FotoEingangPage";
import { useFotoStapel } from "../hooks/useMedien";
import PlanungPage from "../pages/PlanungPage";
import TeamPage from "../pages/TeamPage";
import TeamMemberPage from "../pages/TeamMemberPage";
import KundenPage from "../pages/KundenPage";
import LieferantenPage from "../pages/LieferantenPage";
import AufgabenPage from "../pages/AufgabenPage";
import ProjektePage from "../pages/ProjektePage";
import ProjektDetailPage from "../pages/ProjektDetailPage";
import OwnerDetailPage from "../pages/OwnerDetailPage";
import FinanzenPage from "../pages/FinanzenPage";
import VertraegePage from "../pages/VertraegePage";
import RechnungenPage from "../pages/RechnungenPage";
import RechnungEditorPage from "../pages/RechnungEditorPage";
import BelegAnsichtTelefon from "../pages/BelegAnsichtTelefon";
import { useIstTelefon } from "../hooks/useIstTelefon";
import EinstellungenPage from "../pages/EinstellungenPage";
import KiSpurPage from "../pages/KiSpurPage";
import FeedbackPage from "../pages/FeedbackPage";
import { useNotizAnzahl } from "../hooks/useNotizen";
import { useNachrichtenAnzahl } from "../hooks/useNachrichten";
import NachrichtenBlatt from "./nachrichten/NachrichtenBlatt";
import AngebotAnnahmeFrage from "./projekt/AngebotAnnahmeFrage";
import { NachrichtenAnbieter, useNachrichtenFenster } from "./nachrichten/NachrichtenKontext";
import "@vimaana/ui/shell";

const APP_NAME = "kontor";

const NAV = [
  { group: "Betrieb", items: [
    { to: "/aufgaben", label: "Aufgaben", icon: IconListe },
    { to: "/kunden", label: "Kunden", icon: IconUsers },
    { to: "/lieferanten", label: "Lieferanten", icon: IconLaster },
    { to: "/team", label: "Team", icon: IconTeam },
    { to: "/planung", label: "Planer", icon: IconCalendar },
    { to: "/projekte", label: "Projekte", icon: IconFolder },
  ] },
  { group: "Buchhaltung", items: [
    { to: "/finanzen", label: "Finanzen", icon: IconEuro },
    { to: "/rechnungen", label: "Rechnungen & Angebote", icon: IconFile },
    { to: "/vertraege", label: "Verträge", icon: IconFile },
  ] },
  { group: "System", items: [
    { to: "/einstellungen", label: "Einstellungen", icon: IconGear },
    { to: "/feedback", label: "Feedback", icon: IconCheck },
  ] },
];

/**
 * Auf Telefonbreite passen acht Ziele nicht nebeneinander, ohne unter die
 * 44-px-Touchfläche zu rutschen. Vier Alltagsziele bleiben direkt erreichbar,
 * der Rest liegt einen Tipp entfernt hinter „Mehr" — die Navigation selbst
 * bleibt vollständig.
 */
const TABBAR = [
  { to: "/planung", label: "Planer", icon: IconCalendar },
  { to: "/aufgaben", label: "Aufgaben", icon: IconListe },
  { to: "/projekte", label: "Projekte", icon: IconFolder },
  { to: "/kunden", label: "Kunden", icon: IconUsers },
  { to: "/rechnungen", label: "Rechnungen", icon: IconFile },
];
const TABBAR_REST = [
  { to: "/lieferanten", label: "Lieferanten", icon: IconLaster },
  { to: "/team", label: "Team", icon: IconTeam },
  { to: "/finanzen", label: "Finanzen", icon: IconEuro },
  { to: "/vertraege", label: "Verträge", icon: IconFile },
  { to: "/einstellungen", label: "Einstellungen", icon: IconGear },
  { to: "/feedback", label: "Feedback", icon: IconCheck },
];

/** Das Nachrichtenfenster ist überall erreichbar — sein Zustand lebt über allen Seiten. */
export default function Shell() {
  return (
    <NachrichtenAnbieter>
      <ShellInhalt />
    </NachrichtenAnbieter>
  );
}

/** Wo die schwebende Sprechblase am Telefon stören würde: der Belegeditor hat unten seine eigene Leiste. */
const OHNE_SPRECHBLASE = /^\/rechnungen\/(neu|entwurf|beleg)/;

function ShellInhalt() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const meRes = useMe(APP_NAME) as { data?: { full_name?: string; email?: string } };
  const meName = meRes?.data?.full_name || meRes?.data?.email || "";
  const [mehrOffen, setMehrOffen] = useState(false);
  const offenePunkte = useNotizAnzahl().data?.punkte ?? 0;
  const zuordnenOffen = useFotoStapel().data?.anzahl ?? 0;
  const neueNachrichten = useNachrichtenAnzahl().data?.meine ?? 0;
  const nachrichten = useNachrichtenFenster();
  const loc = useLocation();
  const mehrButtonRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Routenwechsel schließt das Blatt — sonst bleibt es über der neuen Seite liegen.
  useEffect(() => { setMehrOffen(false); }, [loc.pathname]);

  // Escape schließt, der Fokus kehrt auf den auslösenden Knopf zurück.
  useEffect(() => {
    if (!mehrOffen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { setMehrOffen(false); mehrButtonRef.current?.focus(); }
    }
    document.addEventListener("keydown", onKey);
    sheetRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [mehrOffen]);

  const restAktiv = TABBAR_REST.some((it) => loc.pathname.startsWith(it.to));

  return (
    <div className="kontor-layout">
      <a className="kontor-skip-link" href="#kontor-inhalt">Zum Inhalt springen</a>

      <aside className="kontor-sidebar">
        <div className="kontor-brand">
          <div className="kontor-logo">VG</div>
          <div className="kontor-brand-text">
            <div className="kontor-brand-name">Vimaana Garten</div>
            <div className="kontor-brand-subtitle">Kontor</div>
          </div>
        </div>
        <nav className="kontor-nav" aria-label="Hauptnavigation">
          {NAV.map((g) => (
            <div className="kontor-nav-group" key={g.group}>
              <div className="kontor-group-label">{g.group}</div>
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} title={it.label} className={({ isActive }) => `kontor-nav-item${isActive ? " active" : ""}`}>
                  <it.icon /><span>{it.label}</span>
                  {it.to === "/aufgaben" && offenePunkte > 0 && <em className="kontor-nav-zahl">{offenePunkte}</em>}
                </NavLink>
              )).flatMap((el, i) => g.items[i].to === "/aufgaben" ? [el, (
                <button
                  key="nachrichten" type="button" title="Nachrichten"
                  className={`kontor-nav-item kontor-nav-knopf${nachrichten.offen ? " active" : ""}`}
                  onClick={() => nachrichten.oeffnen()}
                >
                  <IconBlase /><span>Nachrichten</span>
                  {neueNachrichten > 0 && <em className="kontor-nav-zahl ist-neu">{neueNachrichten}</em>}
                </button>
              )] : [el])}
            </div>
          ))}
        </nav>
        <div className="kontor-sidebar-footer">
          <button
            className="kontor-theme-toggle"
            type="button"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Auf helles Design wechseln" : "Auf dunkles Design wechseln"}
            title={theme === "dark" ? "Helles Design" : "Dunkles Design"}
          >
            <span aria-hidden="true">{theme === "dark" ? "☀" : "☾"}</span>
            <span className="kontor-footer-label">{theme === "dark" ? "Hell" : "Dunkel"}</span>
          </button>
          {/* Ein Nebeneingang wie der Theme-Schalter, kein Navigationspunkt (Benjamin, 13.10.2026). */}
          <button className="kontor-reload kontor-fotos-knopf" type="button" onClick={() => navigate("/fotos")} title="Baustellenfotos einsortieren">
            <IconKamera />
            <span className="kontor-footer-label">Fotos</span>
            {zuordnenOffen > 0 && <em className="kontor-fotos-zahl" aria-label={`${zuordnenOffen} zum Zuordnen`}>{zuordnenOffen}</em>}
          </button>
          <div className="kontor-footer-row">
            <span className="kontor-api-pill">API: verbunden</span>
            <NavLink to="/profil" className="kontor-profile-link">{meName || "Profil"} ›</NavLink>
          </div>
        </div>
      </aside>

      <main className="kontor-main" id="kontor-inhalt">
        <Routes>
          <Route path="/planung" element={<PlanungPage />} />
          <Route path="/aufgaben" element={<AufgabenPage />} />
          {/* Leeds stehen seit 25.09.2026 nur noch in den Nachrichten — alte Links landen bei den Kunden. */}
          <Route path="/leeds" element={<Navigate to="/kunden" replace />} />
          <Route path="/kunden" element={<KundenPage />} />
          <Route path="/lieferanten" element={<LieferantenPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/owner/:ownerId" element={<OwnerDetailPage />} />
          <Route path="/team/:memberId" element={<TeamMemberPage />} />
          <Route path="/projekte" element={<ProjektePage />} />
          <Route path="/projekte/:id" element={<ProjektDetailPage />} />
          <Route path="/fotos" element={<FotoEingangPage />} />
          <Route path="/finanzen" element={<FinanzenPage />} />
          <Route path="/vertraege" element={<VertraegePage />} />
          <Route path="/rechnungen" element={<RechnungenPage />} />
          {/* Am Telefon wird nicht bearbeitet (Benjamin, 20.10.2026): dieselben
              Adressen zeigen dort eine Ansicht statt des Editors. */}
          <Route path="/rechnungen/neu" element={<BelegEditorOderAnsicht />} />
          <Route path="/rechnungen/entwurf/:draftId" element={<BelegEditorOderAnsicht />} />
          {/* Derselbe Editor fuer einen schon festgeschriebenen Beleg. Ein
              zweiter Editor waere ein zweiter Ort, an dem Rechnungen anders
              aussehen — es gibt nur einen. */}
          <Route path="/rechnungen/beleg/:invoiceId/bearbeiten" element={<BelegEditorOderAnsicht />} />
          <Route path="/einstellungen" element={<EinstellungenPage />} />
          <Route path="/einstellungen/ki-spur" element={<KiSpurPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/profil" element={<ProfileCard appName={APP_NAME} appTitle="Kontor · Konto" />} />
          <Route path="/change-password" element={<ChangePasswordCard appName={APP_NAME} redirectAfter="/planung" />} />
          <Route path="*" element={<Navigate to="/planung" replace />} />
        </Routes>
      </main>

      {/* Telefon-Navigation — auf Tablet/Desktop per CSS ausgeblendet. */}
      <nav className="kontor-tabbar" aria-label="Hauptnavigation (mobil)">
        {TABBAR.map((it) => (
          <NavLink key={it.to} to={it.to} className={({ isActive }) => `kontor-tab${isActive ? " active" : ""}`}>
            <it.icon /><span>{it.label}</span>
          </NavLink>
        ))}
        <button
          ref={mehrButtonRef}
          type="button"
          className={`kontor-tab${restAktiv || mehrOffen ? " active" : ""}`}
          aria-expanded={mehrOffen}
          aria-haspopup="dialog"
          onClick={() => setMehrOffen((v) => !v)}
        >
          <IconMore /><span>Mehr</span>
        </button>
      </nav>

      {/* Telefon: die Sprechblase schwebt über der Tab-Leiste (nachrichten.css). */}
      <button
        type="button"
        className={"nb-knopf" + (nachrichten.offen || OHNE_SPRECHBLASE.test(loc.pathname) ? " ist-versteckt" : "")}
        aria-label={neueNachrichten > 0 ? `Nachrichten, ${neueNachrichten} neu` : "Nachrichten"}
        onClick={() => nachrichten.oeffnen()}
      >
        <IconBlase />
        {neueNachrichten > 0 && <em className="nb-zahl">{neueNachrichten}</em>}
      </button>
      <NachrichtenBlatt />
      <AngebotAnnahmeFrage />

      {mehrOffen && (
        <div className="kontor-sheet-backdrop" onClick={() => setMehrOffen(false)}>
          <div
            className="kontor-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Weitere Bereiche"
            ref={sheetRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="kontor-sheet-griff" aria-hidden="true" />
            {TABBAR_REST.map((it) => (
              <NavLink key={it.to} to={it.to} className={({ isActive }) => `kontor-sheet-item${isActive ? " active" : ""}`}>
                <it.icon /><span>{it.label}</span>
              </NavLink>
            ))}
            <NavLink to="/profil" className="kontor-sheet-item">
              <IconUser /><span>{meName || "Profil"}</span>
            </NavLink>
            <div className="kontor-sheet-actions">
              <button type="button" onClick={toggleTheme}>
                <span aria-hidden="true">{theme === "dark" ? "☀" : "☾"}</span> {theme === "dark" ? "Helles Design" : "Dunkles Design"}
              </button>
              <button type="button" className="kontor-fotos-knopf" onClick={() => { navigate("/fotos"); setMehrOffen(false); }}>
                <IconKamera /> Fotos
                {zuordnenOffen > 0 && <em className="kontor-fotos-zahl">{zuordnenOffen}</em>}
              </button>
            </div>
            <button type="button" className="kontor-sheet-close" onClick={() => setMehrOffen(false)}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Kleines Fotosymbol für den Nebeneingang zur Seite „Fotos“. */
function IconKamera() {
  return (
    <svg className="kontor-fotos-icon" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8h3l1.6-2.2h6.8L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13.3" r="3.4" />
    </svg>
  );
}

/** Editor am Computer, Ansicht am Telefon — Grenze 760 px wie die Shell. */
function BelegEditorOderAnsicht() {
  return useIstTelefon() ? <BelegAnsichtTelefon /> : <RechnungEditorPage />;
}
